/**
 * Cloud-mediated Cast session client (web ↔ TV receiver apps).
 *
 * Why cloud: browsers cannot call Roku ECP (LAN :8060) due to CORS / no
 * public browser API. Fire TV / Tizen / webOS likewise need a receiver app
 * that pulls an authenticated session — not invented LAN discovery.
 *
 * This client builds payloads and talks to the documented Cast cloud API.
 * It does NOT mark Roku/Fire/Samsung/LG as available or connected.
 * Operational playback still requires the branded TV channel/app.
 */

import {
  CastProviderId,
  createCastReceiverSession,
  createReceiverControlMessage,
  normalizeWhiteLabelCastConfig,
} from "./CastReceiverContract";

export var CLOUD_CAST_API_PATHS = Object.freeze({
  create: "/userApi/cast/sessions/create",
  get: "/userApi/cast/sessions/get",
  control: "/userApi/cast/sessions/control",
  claim: "/userApi/cast/sessions/claim",
  disconnect: "/userApi/cast/sessions/disconnect",
  receiverPoll: "/userApi/cast/sessions/receiver/poll",
  receiverReport: "/userApi/cast/sessions/receiver/report",
});

/**
 * Build a cloud cast session create body (sender → API).
 * mediaRef must be an authorized playback grant — never long-lived secrets.
 *
 * @param {object} partial
 * @param {object} [whiteLabel]
 * @returns {object}
 */
export function buildCloudCastSessionCreate(partial, whiteLabel) {
  partial = partial || {};
  var wl = normalizeWhiteLabelCastConfig(whiteLabel || partial.whiteLabel || {});
  var session = createCastReceiverSession(
    Object.assign({}, partial, {
      applicationKey: wl.applicationKey,
      applicationName: wl.applicationName,
      logo: wl.logo,
      receiverApplicationId: wl.receiverApplicationId,
      provider: partial.provider || CastProviderId.ROKU,
    })
  );
  return {
    protocolVersion: 1,
    provider: session.provider,
    application: session.application,
    contentId: session.contentId,
    contentType: session.contentType,
    title: session.title,
    artwork: session.artwork,
    media: session.media,
    position: session.position,
    volume: session.volume,
    muted: session.muted,
    state: session.state,
    // Short-lived pairing for TV claim when same-account SSO is unavailable
    pairingEnabled: partial.pairingEnabled !== false,
    ttlSeconds: partial.ttlSeconds != null ? partial.ttlSeconds : 900,
    phase: partial.phase || 1,
  };
}

/**
 * Roku ECP deep-link template for LAN/dev tools / native shells only.
 * Browsers must NOT call this URL (CORS-blocked). Documented for receiver QA.
 *
 * @param {object} opts
 * @param {string} opts.deviceIp Roku LAN IP
 * @param {string} [opts.channelId] channel id or "dev"
 * @param {string} opts.contentId deep-link contentId (often cloud sessionId)
 * @param {string} [opts.mediaType] movie|episode|live|…
 * @param {"launch"|"input"} [opts.ecpCommand]
 * @returns {string}
 */
export function buildRokuEcpDeepLinkUrl(opts) {
  opts = opts || {};
  var ip = String(opts.deviceIp || "").trim();
  var channelId = String(opts.channelId || "dev").trim() || "dev";
  var contentId = encodeURIComponent(String(opts.contentId || "").trim());
  var mediaType = encodeURIComponent(String(opts.mediaType || "movie").trim());
  var cmd = opts.ecpCommand === "input" ? "input" : "launch";
  if (!ip || !contentId) {
    return "";
  }
  if (cmd === "input") {
    return (
      "http://" +
      ip +
      ":8060/input?contentId=" +
      contentId +
      "&mediaType=" +
      mediaType
    );
  }
  return (
    "http://" +
    ip +
    ":8060/launch/" +
    encodeURIComponent(channelId) +
    "?contentId=" +
    contentId +
    "&mediaType=" +
    mediaType
  );
}

/**
 * Deep-link params the Roku channel Main()/RunUserInterface must accept.
 * contentId maps to cloud session id OR catalog content id per Phase 1 spec.
 *
 * @param {object} session createCastReceiverSession() or cloud create response
 * @returns {object}
 */
export function buildRokuDeepLinkParams(session) {
  session = session || {};
  return {
    contentId: session.sessionId || session.contentId || "",
    mediaType: mapContentTypeToRokuMediaType(session.contentType),
    // Custom keys for channel (also passable via ECP when testing from LAN tools)
    sessionId: session.sessionId || "",
    applicationKey:
      (session.application && session.application.applicationKey) ||
      session.applicationKey ||
      "",
    pairingCode: session.pairingCode || "",
  };
}

export function mapContentTypeToRokuMediaType(contentType) {
  if (contentType === "live") return "live";
  if (contentType === "audio") return "shortFormVideo";
  return "movie";
}

/**
 * @param {object} [options]
 * @param {function} [options.fetchImpl]
 * @param {string} [options.baseUrl]
 * @param {function} [options.getAuthParams] () => { id, token, ... }
 */
export function createCloudCastSessionClient(options) {
  options = options || {};
  var fetchImpl =
    options.fetchImpl ||
    (typeof fetch !== "undefined" ? fetch.bind(typeof window !== "undefined" ? window : global) : null);
  var baseUrl = options.baseUrl || "";
  var getAuthParams =
    options.getAuthParams ||
    function () {
      return {};
    };

  function post(path, body) {
    if (!fetchImpl) {
      return Promise.reject(
        new Error("Cloud cast session API requires fetch (not available)")
      );
    }
    var auth = getAuthParams() || {};
    var payload = Object.assign({}, auth, body || {});
    return fetchImpl(baseUrl + path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok || (json && json.success === false)) {
          var err = new Error(
            (json && (json.error_messages || json.message)) ||
              "Cast cloud session request failed"
          );
          err.code = (json && json.error_code) || res.status;
          err.payload = json;
          throw err;
        }
        return json && json.data != null ? json.data : json;
      });
    });
  }

  return {
    paths: CLOUD_CAST_API_PATHS,
    buildCreateBody: buildCloudCastSessionCreate,
    buildRokuEcpDeepLinkUrl: buildRokuEcpDeepLinkUrl,
    buildRokuDeepLinkParams: buildRokuDeepLinkParams,
    createSession: function (partial, whiteLabel) {
      return post(
        CLOUD_CAST_API_PATHS.create,
        buildCloudCastSessionCreate(partial, whiteLabel)
      );
    },
    getSession: function (sessionId) {
      return post(CLOUD_CAST_API_PATHS.get, { sessionId: sessionId });
    },
    sendControl: function (action, session, extra) {
      var msg = createReceiverControlMessage(action, session, extra);
      return post(CLOUD_CAST_API_PATHS.control, msg);
    },
    claimSession: function (pairingCode, receiverMeta) {
      return post(
        CLOUD_CAST_API_PATHS.claim,
        Object.assign({ pairingCode: pairingCode }, receiverMeta || {})
      );
    },
    disconnect: function (sessionId) {
      return post(CLOUD_CAST_API_PATHS.disconnect, { sessionId: sessionId });
    },
    /**
     * Documented for native/TV shells — browser SPA does not call these.
     * Roku channel posts claim/poll/report without web cookies.
     */
    receiverPaths: {
      poll: CLOUD_CAST_API_PATHS.receiverPoll,
      report: CLOUD_CAST_API_PATHS.receiverReport,
    },
  };
}

export default createCloudCastSessionClient;
